import { useState, useEffect } from "react";
import toast from "../../../utils/toast";
import {
  getFundRequestStats,
  getFundRequestTenantList,
  approveFundRequest,
  rejectFundRequest,
} from "../../../services/propertyOwner/propertyOwnerServices";

export default function FundRequests() {
  const [stats, setStats] = useState(null);
  const [fundData, setFundData] = useState(null);
  const [listLoading, setListLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending");
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchInput, setSearchInput] = useState("");

  const [filters, setFilters] = useState({
    StatusId: "1",
    FromDate: "",
    ToDate: "",
    Search: "",
    Page: 1,
    PageSize: 20,
  });

  const [historyFilters, setHistoryFilters] = useState({
    FromDate: "",
    ToDate: "",
    StatusId: "",
    Search: "",
  });

  const [approveTarget, setApproveTarget] = useState(null);
  const [approveLoading, setApproveLoading] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectLoading, setRejectLoading] = useState(false);
  const [proofItem, setProofItem] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchList();
  }, [filters]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => {
        if (prev.Search === searchInput) return prev;
        return { ...prev, Search: searchInput, Page: 1 };
      });
    }, 2000);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchStats = async () => {
    try {
      const res = await getFundRequestStats();
      if (res.statusCode === 1) setStats(res.data);
    } catch (err) {
      console.log("Failed to fetch stats");
    }
  };

  const fetchList = async () => {
    setListLoading(true);
    try {
      const params = { ...filters };
      if (!params.StatusId) delete params.StatusId;
      if (!params.FromDate) delete params.FromDate;
      if (!params.ToDate) delete params.ToDate;
      if (!params.Search) delete params.Search;

      const res = await getFundRequestTenantList({ params });
      if (res.statusCode === 1) {
        setFundData(res.data);
        setTotalCount(res.data.totalCount);
        setTotalPages(res.data.totalPages);
      }
    } catch (err) {
      console.log("Failed to fetch fund requests");
    } finally {
      setListLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchInput("");
    setHistoryFilters({ FromDate: "", ToDate: "", StatusId: "", Search: "" });
    setFilters((prev) => ({
      ...prev,
      StatusId: tab === "pending" ? "1" : "0",
      FromDate: "",
      ToDate: "",
      Search: "",
      Page: 1,
    }));
  };

  const handlePageChange = (page) => {
    setFilters((prev) => ({ ...prev, Page: page }));
  };

  const handleHistorySearch = () => {
    setFilters((prev) => ({
      ...prev,
      StatusId: historyFilters.StatusId || "0",
      FromDate: historyFilters.FromDate,
      ToDate: historyFilters.ToDate,
      Search: historyFilters.Search,
      Page: 1,
    }));
  };

  const handleHistoryClear = () => {
    setHistoryFilters({ FromDate: "", ToDate: "", StatusId: "", Search: "" });
    setFilters((prev) => ({
      ...prev,
      StatusId: "0",
      FromDate: "",
      ToDate: "",
      Search: "",
      Page: 1,
    }));
  };

  const formatDate = (iso) =>
    iso
      ? new Date(iso).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "—";

  const handleApproveConfirm = async () => {
    setApproveLoading(true);
    try {
      const res = await approveFundRequest(approveTarget.requestId);
      if (res.statusCode === 1) {
        toast.success(res.message || "Request approved.");
        setApproveTarget(null);
        fetchList();
        fetchStats();
      } else {
        toast.error(res.message || "Failed to approve.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setApproveLoading(false);
    }
  };

  const handleRejectConfirm = async () => {
    setRejectLoading(true);
    try {
      const res = await rejectFundRequest(rejectTarget.requestId, {
        reason: rejectReason,
      });
      if (res.statusCode === 1) {
        toast.success(res.message);
        setRejectTarget(null);
        setRejectReason("");
        fetchList();
        fetchStats();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setRejectLoading(false);
    }
  };

  const handleDownloadProof = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = url.split("/").pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.log("Download failed", err);
    }
  };

  const items = fundData?.items ?? [];

  const historySummary = {
    total: totalCount,
    approved: items.filter((i) => i.statusId === 2).length,
    rejected: items.filter((i) => i.statusId === 3).length,
    approvedAmt: items
      .filter((i) => i.statusId === 2)
      .reduce((s, i) => s + i.amount, 0),
    rejectedAmt: items
      .filter((i) => i.statusId === 3)
      .reduce((s, i) => s + i.amount, 0),
    sum: items.reduce((s, i) => s + i.amount, 0),
  };

  return (
    <>
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title" id="page-title">
              Fund Requests
            </div>
            <div className="page-sub" id="page-sub">
              Tenant payment requests — pending and history
            </div>
          </div>
          <div className="topbar-right" id="topbar-right" />
        </div>

        <div className="content" id="content-area">
          <div className="stat-grid mb16">
            <div className="stat-card">
              <div className="stat-icon">📥</div>
              <div className="stat-label">Total Requests</div>
              <div className="stat-value">{stats?.totalRequests ?? 0}</div>
              <div className="stat-delta delta-neu">All time</div>
            </div>
            <div
              className="stat-card"
              style={{ cursor: "pointer", border: "1px solid #FCD34D" }}
              onClick={() => handleTabChange("pending")}
            >
              <div className="stat-icon">⏳</div>
              <div className="stat-label">Pending</div>
              <div className="stat-value" style={{ color: "#92400E" }}>
                {stats?.pendingCount ?? 0}
              </div>
              <div className="stat-delta delta-dn">
                ₹{stats?.pendingAmount?.toLocaleString("en-IN") ?? 0} awaiting
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">✅</div>
              <div className="stat-label">Approved</div>
              <div className="stat-value">{stats?.approvedCount ?? 0}</div>
              <div className="stat-delta delta-up">
                ₹{stats?.approvedAmount?.toLocaleString("en-IN") ?? 0} credited
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">💰</div>
              <div className="stat-label">Total Amount</div>
              <div className="stat-value">
                ₹{stats?.totalAmount?.toLocaleString("en-IN") ?? 0}
              </div>
              <div className="stat-delta delta-neu">All requests</div>
            </div>
          </div>

          <div className="card">
            {/* Tab + Search */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 14,
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              <div style={{ display: "flex", gap: 0 }}>
                <button
                  onClick={() => handleTabChange("pending")}
                  style={{
                    padding: "7px 18px",
                    fontSize: 12,
                    fontWeight: 600,
                    border: "1px solid var(--border-mid)",
                    borderRight: "none",
                    borderRadius: "6px 0 0 6px",
                    cursor: "pointer",
                    background:
                      activeTab === "pending"
                        ? "#185FA5"
                        : "var(--bg-secondary)",
                    color:
                      activeTab === "pending"
                        ? "#fff"
                        : "var(--text-secondary)",
                  }}
                >
                  ⏳ Pending ({stats?.pendingCount ?? 0})
                </button>
                <button
                  onClick={() => handleTabChange("history")}
                  style={{
                    padding: "7px 18px",
                    fontSize: 12,
                    fontWeight: 600,
                    border: "1px solid var(--border-mid)",
                    borderRadius: "0 6px 6px 0",
                    cursor: "pointer",
                    background:
                      activeTab === "history"
                        ? "#185FA5"
                        : "var(--bg-secondary)",
                    color:
                      activeTab === "history"
                        ? "#fff"
                        : "var(--text-secondary)",
                  }}
                >
                  📋 History
                </button>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 6,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <input
                  type="text"
                  placeholder="Search name, UTR…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  style={{
                    padding: "5px 10px",
                    fontSize: 12,
                    width: 150,
                    border: "0.5px solid var(--border-mid)",
                    borderRadius: 6,
                  }}
                />
                <button className="btn btn-sm">📤 Export</button>
              </div>
            </div>

            {/* History filters */}
            {activeTab === "history" && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  alignItems: "flex-end",
                  padding: "12px 14px",
                  background: "var(--bg-secondary)",
                  borderRadius: "var(--radius-md)",
                  marginBottom: 12,
                }}
              >
                <div>
                  <label
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "var(--text-secondary)",
                      display: "block",
                      marginBottom: 4,
                    }}
                  >
                    From Date
                  </label>
                  <input
                    type="date"
                    value={historyFilters.FromDate}
                    onChange={(e) =>
                      setHistoryFilters((p) => ({
                        ...p,
                        FromDate: e.target.value,
                      }))
                    }
                    style={{
                      fontSize: 12,
                      padding: "5px 8px",
                      border: "0.5px solid var(--border-mid)",
                      borderRadius: 6,
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "var(--text-secondary)",
                      display: "block",
                      marginBottom: 4,
                    }}
                  >
                    To Date
                  </label>
                  <input
                    type="date"
                    value={historyFilters.ToDate}
                    onChange={(e) =>
                      setHistoryFilters((p) => ({
                        ...p,
                        ToDate: e.target.value,
                      }))
                    }
                    style={{
                      fontSize: 12,
                      padding: "5px 8px",
                      border: "0.5px solid var(--border-mid)",
                      borderRadius: 6,
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "var(--text-secondary)",
                      display: "block",
                      marginBottom: 4,
                    }}
                  >
                    Status
                  </label>
                  <select
                    value={historyFilters.StatusId}
                    onChange={(e) =>
                      setHistoryFilters((p) => ({
                        ...p,
                        StatusId: e.target.value,
                      }))
                    }
                    style={{
                      fontSize: 12,
                      padding: "5px 8px",
                      border: "0.5px solid var(--border-mid)",
                      borderRadius: 6,
                      minWidth: 130,
                    }}
                  >
                    <option value="">All Status</option>
                    <option value="2">✅ Approved</option>
                    <option value="3">✕ Rejected</option>
                    <option value="1">⏳ Pending</option>
                  </select>
                </div>
                <div>
                  <label
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "var(--text-secondary)",
                      display: "block",
                      marginBottom: 4,
                    }}
                  >
                    Tenant / Mobile
                  </label>
                  <input
                    type="text"
                    placeholder="Name or mobile…"
                    value={historyFilters.Search}
                    onChange={(e) =>
                      setHistoryFilters((p) => ({
                        ...p,
                        Search: e.target.value,
                      }))
                    }
                    style={{
                      fontSize: 12,
                      padding: "5px 8px",
                      border: "0.5px solid var(--border-mid)",
                      borderRadius: 6,
                      width: 150,
                    }}
                  />
                </div>
                <div
                  style={{ display: "flex", alignItems: "flex-end", gap: 6 }}
                >
                  <button
                    onClick={handleHistorySearch}
                    style={{
                      background: "#185FA5",
                      color: "#fff",
                      border: "none",
                      borderRadius: 7,
                      padding: "7px 18px",
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    🔍 Search
                  </button>
                  <button
                    onClick={handleHistoryClear}
                    style={{
                      background: "var(--bg-primary)",
                      border: "0.5px solid var(--border-mid)",
                      borderRadius: 7,
                      padding: "7px 12px",
                      fontSize: 12,
                      cursor: "pointer",
                      color: "var(--text-secondary)",
                    }}
                  >
                    ✕ Clear
                  </button>
                </div>
              </div>
            )}

            {/* Table */}
            <div style={{ overflowX: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Requested By</th>
                    <th>Amount</th>
                    <th>Type</th>
                    <th>Mode / Instrument</th>
                    <th>UTR / Ref</th>
                    <th>Date</th>
                    <th>Remark</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {listLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 10 }).map((_, j) => (
                          <td key={j}>
                            <div
                              style={{
                                height: 14,
                                borderRadius: 6,
                                background:
                                  "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
                                backgroundSize: "200% 100%",
                                animation: "shimmer 1.4s infinite",
                                width: j === 1 ? "80%" : "65%",
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : items.length === 0 ? (
                    <tr>
                      <td
                        colSpan={10}
                        style={{
                          textAlign: "center",
                          color: "var(--text-tertiary)",
                          padding: 24,
                        }}
                      >
                        No fund requests found.
                      </td>
                    </tr>
                  ) : (
                    items.map((item, idx) => (
                      <tr key={item.requestId}>
                        <td className="sn-col">
                          {(filters.Page - 1) * filters.PageSize + idx + 1}
                        </td>
                        <td>
                          <div style={{ fontWeight: 600 }}>
                            {item.tenantName}
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              color: "var(--text-secondary)",
                            }}
                          >
                            📱 {item.tenantMobile}
                          </div>
                          {item.roomInfo && (
                            <div
                              style={{
                                fontSize: 10,
                                color: "var(--text-tertiary)",
                              }}
                            >
                              {item.roomInfo}
                            </div>
                          )}
                        </td>
                        <td>
                          <strong style={{ color: "var(--blue)" }}>
                            ₹{item.amount?.toLocaleString("en-IN")}
                          </strong>
                        </td>
                        <td style={{ fontSize: 12 }}>Rent</td>
                        <td>
                          <div style={{ fontSize: 12, fontWeight: 500 }}>
                            {item.paymentModeName}
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              color: "var(--text-tertiary)",
                              marginTop: 2,
                            }}
                          >
                            {item.instrumentName}
                          </div>
                        </td>
                        <td style={{ fontFamily: "monospace", fontSize: 11 }}>
                          {item.utr || "—"}
                        </td>
                        <td style={{ fontSize: 12 }}>
                          {formatDate(item.createdAt)}
                        </td>
                        <td
                          style={{
                            fontSize: 11,
                            color: "var(--text-secondary)",
                          }}
                        >
                          {item.remark || "—"}
                          {item.rejectedReason && (
                            <div
                              style={{
                                fontSize: 10,
                                color: "var(--red, #dc2626)",
                                marginTop: 2,
                              }}
                            >
                              ❌ {item.rejectedReason}
                            </div>
                          )}
                        </td>
                        <td>
                          <span
                            className={`badge ${item.statusId === 1 ? "badge-pending" : item.statusId === 2 ? "badge-active" : "badge-inactive"}`}
                          >
                            {item.status}
                          </span>
                        </td>
                        <td>
                          <div className="td-actions">
                            {item.statusId === 1 && (
                              <>
                                <span
                                  className="action-icon green"
                                  title="Approve"
                                  onClick={() => setApproveTarget(item)}
                                >
                                  <span className="tip">Approve</span>✓
                                </span>
                                <span
                                  className="action-icon red"
                                  title="Reject"
                                  onClick={() => {
                                    setRejectTarget(item);
                                    setRejectReason("");
                                  }}
                                >
                                  <span className="tip">Reject</span>✕
                                </span>
                              </>
                            )}
                            {item.statusId !== 1 && (
                              <span
                                style={{
                                  fontSize: 11,
                                  color: "var(--text-tertiary)",
                                }}
                              >
                                Done
                              </span>
                            )}
                            {item.proofFileUrl && (
                              <span
                                className="action-icon"
                                title="View Proof"
                                onClick={() => setProofItem(item)}
                              >
                                <span className="tip">View Proof</span>👁
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* History Summary */}
            {activeTab === "history" && !listLoading && items.length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 16,
                  padding: "12px 14px",
                  background: "#f8fafc",
                  borderRadius: "0 0 var(--radius-md) var(--radius-md)",
                  borderTop: "1px solid var(--border-light)",
                  marginTop: 8,
                }}
              >
                <div style={{ fontSize: 12 }}>
                  <span style={{ color: "var(--text-secondary)" }}>
                    Total Records:{" "}
                  </span>
                  <strong>{historySummary.total}</strong>
                </div>
                <div style={{ fontSize: 12 }}>
                  <span style={{ color: "var(--text-secondary)" }}>
                    Approved:{" "}
                  </span>
                  <strong style={{ color: "#0F6E56" }}>
                    {historySummary.approved}
                  </strong>
                </div>
                <div style={{ fontSize: 12 }}>
                  <span style={{ color: "var(--text-secondary)" }}>
                    Rejected:{" "}
                  </span>
                  <strong style={{ color: "var(--red)" }}>
                    {historySummary.rejected}
                  </strong>
                </div>
                <div style={{ fontSize: 12 }}>
                  <span style={{ color: "var(--text-secondary)" }}>
                    Approved Amount:{" "}
                  </span>
                  <strong style={{ color: "#0F6E56" }}>
                    ₹{historySummary.approvedAmt?.toLocaleString("en-IN")}
                  </strong>
                </div>
                <div style={{ fontSize: 12 }}>
                  <span style={{ color: "var(--text-secondary)" }}>
                    Rejected Amount:{" "}
                  </span>
                  <strong style={{ color: "var(--red)" }}>
                    ₹{historySummary.rejectedAmt?.toLocaleString("en-IN")}
                  </strong>
                </div>
                <div
                  style={{ fontSize: 13, fontWeight: 700, marginLeft: "auto" }}
                >
                  <span style={{ color: "var(--text-secondary)" }}>
                    Sum of Shown:{" "}
                  </span>
                  <span style={{ color: "#185FA5" }}>
                    ₹{historySummary.sum?.toLocaleString("en-IN")}
                  </span>
                </div>
              </div>
            )}

            {/* Pagination */}
            <div
              style={{
                marginTop: 12,
                display: "flex",
                justifyContent: "space-between",
                fontSize: 12,
                color: "var(--text-secondary)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <select
                  value={filters.PageSize}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      PageSize: Number(e.target.value),
                      Page: 1,
                    }))
                  }
                  style={{ fontSize: 12, padding: "4px 8px" }}
                >
                  <option value={10}>10 / page</option>
                  <option value={20}>20 / page</option>
                  <option value={50}>50 / page</option>
                </select>
                <span>
                  Showing <strong>{items.length}</strong> of{" "}
                  <strong>{totalCount}</strong>
                </span>
              </div>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <button
                  className="btn btn-xs"
                  disabled={filters.Page === 1}
                  onClick={() => handlePageChange(filters.Page - 1)}
                >
                  ‹ Prev
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <span
                      key={page}
                      onClick={() => handlePageChange(page)}
                      style={{
                        padding: "2px 8px",
                        background:
                          filters.Page === page ? "var(--blue)" : "transparent",
                        color:
                          filters.Page === page
                            ? "#fff"
                            : "var(--text-secondary)",
                        borderRadius: 5,
                        fontSize: 11,
                        cursor: "pointer",
                        border:
                          filters.Page === page
                            ? "none"
                            : "1px solid var(--border-mid)",
                      }}
                    >
                      {page}
                    </span>
                  ),
                )}
                <button
                  className="btn btn-xs"
                  disabled={filters.Page === totalPages}
                  onClick={() => handlePageChange(filters.Page + 1)}
                >
                  Next ›
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Approve Confirmation ── */}
      {approveTarget && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 400, maxWidth: "98vw" }}>
            <div className="modal-header">
              <div className="modal-title">Approve Fund Request</div>
              <button
                className="modal-close"
                onClick={() => setApproveTarget(null)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: 14, marginBottom: 8 }}>
                Approve{" "}
                <strong>
                  ₹{approveTarget.amount?.toLocaleString("en-IN")}
                </strong>{" "}
                request from <strong>{approveTarget.tenantName}</strong>?
              </div>
              <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                UTR: {approveTarget.utr}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={() => setApproveTarget(null)}
                disabled={approveLoading}
              >
                Cancel
              </button>
              <button
                className="btn"
                style={{
                  background: "var(--green, #16a34a)",
                  color: "#fff",
                  border: "none",
                }}
                onClick={handleApproveConfirm}
                disabled={approveLoading}
              >
                {approveLoading ? "Approving..." : "✓ Yes, Approve"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reject Modal ── */}
      {rejectTarget && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 420, maxWidth: "98vw" }}>
            <div className="modal-header">
              <div className="modal-title">Reject Fund Request</div>
              <button
                className="modal-close"
                onClick={() => setRejectTarget(null)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: 13, marginBottom: 12 }}>
                Rejecting{" "}
                <strong>₹{rejectTarget.amount?.toLocaleString("en-IN")}</strong>{" "}
                request from <strong>{rejectTarget.tenantName}</strong>
              </div>
              <div className="form-group">
                <label>Reason for rejection (optional)</label>
                <input
                  type="text"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Enter reason…"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={() => setRejectTarget(null)}
                disabled={rejectLoading}
              >
                Cancel
              </button>
              <button
                className="btn"
                style={{
                  background: "var(--red, #dc2626)",
                  color: "#fff",
                  border: "none",
                }}
                onClick={handleRejectConfirm}
                disabled={rejectLoading}
              >
                {rejectLoading ? "Rejecting..." : "✕ Yes, Reject"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Proof Modal ── */}
      {proofItem && (
        <div className="modal-overlay open" onClick={() => setProofItem(null)}>
          <div
            className="modal"
            style={{ width: 500, maxWidth: "98vw" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div className="modal-title">Payment Proof</div>
              <button
                className="modal-close"
                onClick={() => setProofItem(null)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div
                style={{
                  background: "var(--bg-secondary)",
                  borderRadius: 8,
                  padding: "12px 16px",
                  marginBottom: 14,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    marginBottom: 4,
                    fontSize: 13,
                  }}
                >
                  <span
                    style={{ color: "var(--text-secondary)", minWidth: 100 }}
                  >
                    Submitted By:
                  </span>
                  <strong>{proofItem.tenantName}</strong>
                </div>
                <div style={{ display: "flex", gap: 8, fontSize: 13 }}>
                  <span
                    style={{ color: "var(--text-secondary)", minWidth: 100 }}
                  >
                    UTR / Ref:
                  </span>
                  <span style={{ fontFamily: "monospace" }}>
                    {proofItem.utr || "—"}
                  </span>
                </div>
              </div>
              <div
                style={{
                  background: "var(--bg-secondary)",
                  borderRadius: 8,
                  padding: 20,
                  textAlign: "center",
                }}
              >
                <img
                  src={proofItem.proofFileUrl}
                  alt="Payment Proof"
                  style={{
                    maxWidth: "100%",
                    maxHeight: 300,
                    borderRadius: 6,
                    objectFit: "contain",
                    marginBottom: 10,
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                  {proofItem.proofFileUrl?.split("/").pop()}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--text-secondary)",
                    marginBottom: 14,
                  }}
                >
                  Payment proof uploaded by tenant
                </div>
                <div
                  style={{ display: "flex", gap: 8, justifyContent: "center" }}
                >
                  <button
                    className="btn btn-primary"
                    style={{ fontSize: 12 }}
                    onClick={() => handleDownloadProof(proofItem.proofFileUrl)}
                  >
                    ↓ Download
                  </button>
                  <a
                    href={proofItem.proofFileUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="btn"
                    style={{ fontSize: 12 }}
                  >
                    🔍 View Full Size
                  </a>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setProofItem(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
